import React, { useState, useEffect, useCallback } from 'react';
import { servicesApi, staffApi, templatesApi } from '../api/client';
import { Modal, CID, Spin, Empty, useFlash, F, parseErr } from '../components/UI';
import CustomerSearch from '../components/CustomerSearch';
import { Check } from '../components/Icons';

const openWA = (phone, msg) => {
  const n = String(phone || '').replace(/\D/g, '');
  const num = n.length === 10 ? `91${n}` : n;
  if (!num) return;
  window.open(`https://wa.me/${num}?text=${encodeURIComponent(msg)}`, '_blank');
};

const fillTpl = (body, name, extras = {}) =>
  body
    .replace(/\[Customer Name\]/g, name || 'Customer')
    .replace(/\[Service ID\]/g,    extras.serviceId || '')
    .replace(/\[Product\]/g,       extras.product   || '');

const PMODES = ['Cash', 'Card', 'UPI', 'Bank Transfer', 'Free / Warranty'];

export default function Service() {
  const [rows, setRows]   = useState([]); const [busy, setBusy] = useState(true);
  const [showAdd, setAdd] = useState(false); const { show, Flash } = useFlash();
  const [staff, setStaff] = useState([]);
  const [tpls, setTpls]   = useState([]);
  const [form, setForm]   = useState({ customer_id:'', service_type:'Annual Maintenance', assigned_to:'', scheduled_date:'', notes:'' });
  const [cFound, setCF]   = useState(null);
  const [saving, setSaving] = useState(false); const [err, setErr] = useState('');
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  // Completion modal
  const [completing, setCompleting] = useState(null);
  const [compForm, setCompForm] = useState({ amount_received:'', payment_mode:'Cash' });
  const [compSaving, setCompSaving] = useState(false);
  const setComp = k => e => setCompForm(f => ({ ...f, [k]: e.target.value }));

  const load = useCallback(async () => {
    setBusy(true);
    try { const { data } = await servicesApi.list(); setRows(data); }
    finally { setBusy(false); }
  }, []);

  useEffect(() => {
    load();
    staffApi.list().then(({ data }) => setStaff(data.filter(s => s.role === 'field'))).catch(() => {});
    templatesApi.list().then(({ data }) => setTpls(data)).catch(() => {});
  }, [load]);

  const confirmDone = async () => {
    setCompSaving(true);
    try {
      const payload = {
        status: 'completed',
        payment_mode: compForm.payment_mode || undefined,
      };
      if (compForm.amount_received !== '') payload.amount_received = Number(compForm.amount_received);
      const { data } = await servicesApi.update(completing.id, payload);
      setRows(r => r.map(s => s.id === completing.id ? data : s));
      show('Service marked as completed!');
      setCompleting(null);
      setCompForm({ amount_received:'', payment_mode:'Cash' });
    } catch { show('Failed to update.', 'error'); }
    finally { setCompSaving(false); }
  };

  const save = async () => {
    if (!form.customer_id) { setErr('Please select a customer.'); return; }
    setSaving(true); setErr('');
    const custName  = cFound?.full_name || 'Customer';
    const custPhone = cFound?.phone || '';
    try {
      const { data } = await servicesApi.create({
        ...form,
        assigned_to:    form.assigned_to    || undefined,
        scheduled_date: form.scheduled_date || undefined,
      });
      setRows(r => [data, ...r]);
      show(`Service ${data.service_code} created!`);

      if (custPhone) {
        const tpl = tpls.find(t => t.name.toLowerCase().includes('service')) || tpls.find(t => t.name.toLowerCase().includes('follow'));
        const msg = tpl
          ? fillTpl(tpl.body, custName, { serviceId: data.service_code })
          : `Dear ${custName}, your service request (${data.service_code}) for ${form.service_type} has been booked. Our team will contact you shortly. - Sakthi Solar Solutions`;
        openWA(custPhone, msg);
      }

      setAdd(false);
      setForm({ customer_id:'', service_type:'Annual Maintenance', assigned_to:'', scheduled_date:'', notes:'' });
      setCF(null);
    } catch (e) { setErr(parseErr(e, 'Failed to create service.')); }
    finally { setSaving(false); }
  };

  if (busy) return <Spin/>;
  return (
    <div>
      <Flash/>
      <div className="page-hd"><h2>Service Requests</h2><button className="btn btn-primary" onClick={() => setAdd(true)}>+ New Service</button></div>
      <div className="card card-0">
        {rows.length === 0 ? <Empty msg="No service requests yet"/> : (
          <div className="tbl-wrap"><table>
            <thead><tr><th>Date</th><th>ID</th><th>Type</th><th>Assigned</th><th>Scheduled</th><th>Status</th><th>Amt Received</th><th>Payment Mode</th><th>Actions</th></tr></thead>
            <tbody>
              {rows.map(s => (
                <tr key={s.id}>
                  <td style={{ fontSize:12 }}>{new Date(s.created_at).toLocaleDateString('en-IN')}</td>
                  <td style={{ fontFamily:'monospace', fontSize:11 }}>{s.service_code}</td>
                  <td>{s.service_type}</td>
                  <td>{s.assigned_to ? <span className="badge b-info">Assigned</span> : <span className="badge b-gray">Unassigned</span>}</td>
                  <td style={{ fontSize:12 }}>{s.scheduled_date || '—'}</td>
                  <td><span className={`badge ${s.status === 'completed' ? 'b-ok' : s.status === 'pending' ? 'b-warn' : 'b-info'}`}>{s.status}</span></td>
                  <td style={{ fontWeight: s.amount_received ? 600 : 400, color: s.amount_received ? 'var(--green)' : 'var(--text2)' }}>
                    {s.amount_received != null ? `₹${Number(s.amount_received).toLocaleString('en-IN')}` : '—'}
                  </td>
                  <td style={{ fontSize:12 }}>{s.payment_mode || '—'}</td>
                  <td>
                    {s.status !== 'completed'
                      ? <button className="btn btn-sm" onClick={() => { setCompleting(s); setCompForm({ amount_received:'', payment_mode:'Cash' }); }}><Check s={13}/> Done</button>
                      : <span style={{ fontSize:11, color:'var(--text2)' }}>Completed</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table></div>
        )}
      </div>

      {/* ── New Service Modal ── */}
      <Modal open={showAdd} onClose={() => setAdd(false)} title="New Service Request"
        footer={
          <>
            <button className="btn" onClick={() => setAdd(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={save} disabled={saving}>
              {saving ? 'Saving…' : 'Book Service & Notify Customer'}
            </button>
          </>
        }
      >
        {err && <div className="notice n-err" style={{ marginBottom:10 }}>{err}</div>}
        {!form.customer_id
          ? <div style={{ marginBottom:12 }}><CustomerSearch onSelect={c => { setCF(c); setForm(f => ({ ...f, customer_id: c.id })); }}/></div>
          : <div className="notice n-ok" style={{ marginBottom:12, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
              <span><strong>{cFound?.full_name}</strong> <CID code={cFound?.customer_code}/> — WhatsApp confirmation will be sent on booking.</span>
              <button className="btn btn-sm btn-ghost" style={{ padding:'2px 8px' }} onClick={() => { setCF(null); setForm(f => ({ ...f, customer_id:'' })); }}>Change</button>
            </div>
        }
        <div className="fg-2">
          <F label="Service Type">
            <select value={form.service_type} onChange={set('service_type')}>
              {['Annual Maintenance','Installation','Repair','Battery Replacement','Inspection'].map(t => <option key={t}>{t}</option>)}
            </select>
          </F>
          <F label="Assign To (Field Staff)">
            <select value={form.assigned_to} onChange={set('assigned_to')}>
              <option value="">Unassigned</option>
              {staff.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </F>
          <F label="Scheduled Date"><input type="date" value={form.scheduled_date} onChange={set('scheduled_date')}/></F>
          <F label="Notes" full><textarea value={form.notes} onChange={set('notes')}/></F>
        </div>
      </Modal>

      {/* ── Mark Completed Modal ── */}
      <Modal open={!!completing} onClose={() => setCompleting(null)} title={`Complete Service — ${completing?.service_code}`} sm
        footer={
          <>
            <button className="btn" onClick={() => setCompleting(null)}>Cancel</button>
            <button className="btn btn-primary" onClick={confirmDone} disabled={compSaving}>
              {compSaving ? 'Saving…' : 'Mark as Completed'}
            </button>
          </>
        }
      >
        <div className="fg-2">
          <F label="Amount Received (₹)">
            <input type="number" min="0" placeholder="0 if free / warranty"
              value={compForm.amount_received} onChange={setComp('amount_received')}/>
          </F>
          <F label="Payment Mode">
            <select value={compForm.payment_mode} onChange={setComp('payment_mode')}>
              {PMODES.map(m => <option key={m}>{m}</option>)}
            </select>
          </F>
        </div>
      </Modal>
    </div>
  );
}
